import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import Locals from '../providers/Locals';
import { captureApiError } from '@odo/shared-ui/observability/captureApiError';
import { redirectToLogin } from './redirectToLogin';

const API_BASE_URL = Locals.config().base_api_url;

// Multi-value filters carry raw names - practices, teams, tags - and those can
// contain a comma ("Christine Bloss, DDS"). The list stays comma-separated on
// the wire, but a comma *inside* a value is escaped first, so the backend's
// split no longer tears the name into fragments that match nothing.
//
// Escaping rather than repeated/bracketed params: express parses those through
// qs, whose arrayLimit silently turns a list of 21+ values into an index-keyed
// object that the validators read as "no filter at all". Escaping has no such
// ceiling, matches how the work-queue URL params encode the same values, and
// leaves legacy comma-joined requests decoding exactly as before.
const ESCAPED_COMMA = '%2C';

function serializeParams(params: Record<string, unknown>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        const encoded = Array.isArray(value)
            ? value
                  .filter((item) => item !== undefined && item !== null)
                  .map((item) => String(item).replaceAll(',', ESCAPED_COMMA))
                  .join(',')
            : String(value);
        search.append(key, encoded);
    }
    return search.toString();
}

const apiV2 = axios.create({
    baseURL: API_BASE_URL + '/api/v2',
    headers: { 'Content-Type': 'application/json' },
    paramsSerializer: { serialize: serializeParams }
});

const requestInterceptor = (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('odo_login_token');
    if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
};

const responseInterceptor = (response: AxiosResponse) => response;

const responseErrorInterceptor = (error: unknown) => {
    const axiosError = error as { response?: { status?: number }; config?: { method?: string } };
    const status = axiosError.response?.status;
    const method = String(axiosError.config?.method ?? 'get').toLowerCase();

    if (status === 401 && globalThis.location.pathname !== '/login') {
        redirectToLogin();
    } else if (
        status === 403 &&
        method === 'get' &&
        globalThis.location.pathname !== '/forbidden'
    ) {
        // Only a page/data fetch (GET) that is forbidden bounces to /forbidden.
        // A forbidden mutation (POST/PUT/…) is an action-level denial — captured
        // here and surfaced as a toast by the caller instead of navigating away.
        captureApiError(error);
        globalThis.location.href = '/forbidden';
    } else {
        captureApiError(error);
    }

    return Promise.reject(error);
};

apiV2.interceptors.request.use(requestInterceptor);
apiV2.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

export default apiV2;
